/**
 * Rod Armour Multi-Buy Offer Web Component
 *
 * Supports two offer types:
 *   - tiered_percent : Buy X save Y%
 *   - bxgy           : Buy X Get Y Free
 *
 * Handles tier card selection, progress text updates, multi-size selector
 * with +/- controls, and "Add All" to cart via /cart/add.js.
 */

if (!customElements.get('multi-buy-offer')) {
  class MultiBuyOffer extends HTMLElement {
    /* ── Private state ── */

    /** @type {AbortController|null} */
    #fetchController = null;

    /** @type {AbortController|null} */
    #eventController = null;

    /** @type {NodeListOf<HTMLElement>|null} */
    #tierCards = null;

    /** @type {HTMLElement|null} */
    #progressEl = null;

    /** @type {HTMLElement|null} */
    #sizeRows = null;

    /** @type {HTMLElement|null} */
    #totalEl = null;

    /** @type {HTMLElement|null} */
    #atcBtn = null;

    /** @type {{ qty: number, pct: number }[]} */
    #tiers = [];

    /** @type {string} */
    #offerType = 'tiered_percent';

    /** @type {number} */
    #bxgyBuy = 0;

    /** @type {number} */
    #bxgyGet = 0;

    /** @type {number|null} */
    #successTimeout = null;

    /* ── Lifecycle ── */

    connectedCallback() {
      this.#eventController = new AbortController();
      const signal = this.#eventController.signal;

      this.#offerType = this.dataset.offerType || 'tiered_percent';

      // Parse tier data from data attributes
      this.#tiers = this.#parseTiers();

      // Parse BXGY data
      this.#bxgyBuy = parseInt(this.dataset.bxgyBuy, 10) || 0;
      this.#bxgyGet = parseInt(this.dataset.bxgyGet, 10) || 0;

      // Cache DOM references
      this.#tierCards = this.querySelectorAll('[data-tier-select]');
      this.#progressEl = this.querySelector('[data-multi-buy-progress]');
      this.#sizeRows = this.querySelector('[data-size-rows]');
      this.#totalEl = this.querySelector('[data-multi-size-total]');
      this.#atcBtn = this.querySelector('[data-multi-size-atc]');

      // Set up tier card interactions
      this.#initTierCards(signal);

      // Set up multi-size selector interactions
      this.#initMultiSizeSelector(signal);

      // Watch the page quantity input for changes
      this.#observePageQuantity(signal);

      // Initial state
      this.#syncProgressFromPageQuantity();
    }

    disconnectedCallback() {
      // Abort all event listeners
      if (this.#eventController) {
        this.#eventController.abort();
        this.#eventController = null;
      }

      // Abort any in-flight fetch
      if (this.#fetchController) {
        this.#fetchController.abort();
        this.#fetchController = null;
      }

      // Clear timeouts
      if (this.#successTimeout) {
        clearTimeout(this.#successTimeout);
        this.#successTimeout = null;
      }

      // Clear references
      this.#tierCards = null;
      this.#progressEl = null;
      this.#sizeRows = null;
      this.#totalEl = null;
      this.#atcBtn = null;
      this.#tiers = [];
    }

    /* ── Tier Parsing ── */

    /** @returns {{ qty: number, pct: number }[]} */
    #parseTiers() {
      const tiers = [];
      for (let i = 1; i <= 3; i++) {
        const qty = parseInt(this.dataset[`tier${i}Qty`], 10) || 0;
        const pct = parseInt(this.dataset[`tier${i}Pct`], 10) || 0;
        if (qty > 0 && pct > 0) {
          tiers.push({ qty, pct });
        }
      }
      // Sort ascending by quantity
      tiers.sort((a, b) => a.qty - b.qty);
      return tiers;
    }

    /* ── Tier Card Interactions ── */

    /** @param {AbortSignal} signal */
    #initTierCards(signal) {
      if (!this.#tierCards || this.#tierCards.length === 0) return;

      this.#tierCards.forEach((card) => {
        // Click handler
        card.addEventListener('click', () => {
          this.#selectTier(card);
        }, { signal });

        // Keyboard: Enter and Space
        card.addEventListener('keydown', (e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            this.#selectTier(card);
          }
          // Arrow keys for radiogroup navigation
          if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
            e.preventDefault();
            this.#moveTierFocus(card, 1);
          }
          if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
            e.preventDefault();
            this.#moveTierFocus(card, -1);
          }
        }, { signal });
      });
    }

    /** @param {HTMLElement} card */
    #selectTier(card) {
      const qty = parseInt(card.dataset.qty, 10) || 1;

      // Update aria-checked for radio pattern
      this.#tierCards?.forEach((c) => {
        c.setAttribute('aria-checked', 'false');
        c.setAttribute('tabindex', '-1');
      });
      card.setAttribute('aria-checked', 'true');
      card.setAttribute('tabindex', '0');
      card.focus();

      // Set the page's main quantity input
      this.#setPageQuantity(qty);

      // Update progress text
      this.#updateProgress(qty);
    }

    /**
     * Move focus between tier cards
     * @param {HTMLElement} current
     * @param {number} direction - 1 for next, -1 for previous
     */
    #moveTierFocus(current, direction) {
      if (!this.#tierCards) return;
      const cards = Array.from(this.#tierCards);
      const idx = cards.indexOf(current);
      const nextIdx = (idx + direction + cards.length) % cards.length;
      const next = cards[nextIdx];
      if (next) {
        this.#selectTier(next);
      }
    }

    /* ── Page Quantity Integration ── */

    /** @returns {HTMLInputElement|null} */
    #getPageQuantityInput() {
      // Look for the standard Horizon quantity input within the same product section
      const section = this.closest('.shopify-section');
      if (section) {
        const input = section.querySelector('quantity-selector-component input[type="number"], .quantity-selector input[type="number"], input[name="quantity"]');
        if (input) return /** @type {HTMLInputElement} */ (input);
      }
      // Fallback: look on the full page
      return document.querySelector('quantity-selector-component input[type="number"]');
    }

    /** @param {number} qty */
    #setPageQuantity(qty) {
      const input = this.#getPageQuantityInput();
      if (!input) return;

      // Use native setter to trigger framework reactivity
      const nativeSetter = Object.getOwnPropertyDescriptor(HTMLInputElement.prototype, 'value')?.set;
      if (nativeSetter) {
        nativeSetter.call(input, String(qty));
      } else {
        input.value = String(qty);
      }

      // Dispatch input + change events so other components pick up the change
      input.dispatchEvent(new Event('input', { bubbles: true }));
      input.dispatchEvent(new Event('change', { bubbles: true }));
    }

    /** @returns {number} */
    #getPageQuantity() {
      const input = this.#getPageQuantityInput();
      return input ? parseInt(input.value, 10) || 1 : 1;
    }

    /** @param {AbortSignal} signal */
    #observePageQuantity(signal) {
      const input = this.#getPageQuantityInput();
      if (!input) return;

      const handler = () => {
        const qty = parseInt(input.value, 10) || 1;
        this.#highlightMatchingTier(qty);
        this.#updateProgress(qty);
      };

      input.addEventListener('input', handler, { signal });
      input.addEventListener('change', handler, { signal });
    }

    #syncProgressFromPageQuantity() {
      const qty = this.#getPageQuantity();
      this.#highlightMatchingTier(qty);
      this.#updateProgress(qty);
    }

    /** @param {number} qty */
    #highlightMatchingTier(qty) {
      if (!this.#tierCards || this.#tierCards.length === 0) return;

      // Find the best matching tier (highest tier whose qty <= current qty)
      let matchedCard = null;
      this.#tierCards.forEach((card) => {
        const tierQty = parseInt(card.dataset.qty, 10) || 0;
        if (qty >= tierQty) {
          matchedCard = card;
        }
      });

      // Update aria-checked
      this.#tierCards.forEach((card) => {
        const isMatch = card === matchedCard;
        card.setAttribute('aria-checked', isMatch ? 'true' : 'false');
        card.setAttribute('tabindex', isMatch ? '0' : '-1');
      });
    }

    /* ── Progress Text ── */

    /** @param {number} currentQty */
    #updateProgress(currentQty) {
      if (!this.#progressEl) return;

      if (this.#offerType === 'tiered_percent') {
        this.#updateTieredProgress(currentQty);
      } else if (this.#offerType === 'bxgy') {
        this.#updateBxgyProgress(currentQty);
      }
    }

    /** @param {number} qty */
    #updateTieredProgress(qty) {
      if (this.#tiers.length === 0) return;

      // Find active tier and next tier
      let activeTier = null;
      let nextTier = null;

      for (const tier of this.#tiers) {
        if (qty >= tier.qty) {
          activeTier = tier;
        } else if (!nextTier) {
          nextTier = tier;
        }
      }

      if (!activeTier && nextTier) {
        // Below first tier
        const remaining = nextTier.qty - qty;
        this.#setProgressText(
          this.#formatTemplate(this.dataset.progressUnlockTemplate, {
            need: remaining,
            pct: nextTier.pct
          }, `Add ${remaining} more to unlock ${nextTier.pct}% off`),
          'approaching'
        );
      } else if (activeTier && nextTier) {
        // At one tier, approaching the next
        const remaining = nextTier.qty - qty;
        this.#setProgressText(
          this.#formatTemplate(this.dataset.progressNextTemplate, {
            need: remaining,
            pct: nextTier.pct
          }, `Add ${remaining} more for ${nextTier.pct}% off`),
          'approaching'
        );
      } else if (activeTier && !nextTier) {
        // At the highest tier
        this.#setProgressText(
          this.#formatTemplate(this.dataset.progressMaxTemplate, {
            pct: activeTier.pct
          }, `${activeTier.pct}% off applied!`),
          'met'
        );
      }
    }

    /** @param {number} qty */
    #updateBxgyProgress(qty) {
      const totalNeeded = this.#bxgyBuy + this.#bxgyGet;
      if (totalNeeded <= 0) return;

      const freeCount = Math.floor(qty / totalNeeded) * this.#bxgyGet;
      const remainder = qty % totalNeeded;

      if (freeCount > 0) {
        const unlockedTemplate = freeCount === 1
          ? this.dataset.bxgyUnlockedOneTemplate
          : this.dataset.bxgyUnlockedOtherTemplate;
        this.#setProgressText(
          this.#formatTemplate(unlockedTemplate, {
            count: freeCount
          }, `Deal unlocked: ${freeCount} free item${freeCount > 1 ? 's' : ''}.`),
          'met'
        );
      } else {
        const remaining = remainder === 0 && qty > 0 ? totalNeeded : totalNeeded - remainder;
        this.#setProgressText(
          this.#formatTemplate(this.dataset.bxgyNeedTemplate, {
            need: remaining,
            count: this.#bxgyGet
          }, `Add ${remaining} more to get ${this.#bxgyGet} free`),
          'approaching'
        );
      }
    }

    /**
     * @param {string|undefined} template
     * @param {Record<string, string|number>} values
     * @param {string} fallback
     */
    #formatTemplate(template, values, fallback) {
      const source = template || fallback;
      const formatted = Object.keys(values).reduce((text, key) => {
        return text
          .replaceAll(`__${key}__`, String(values[key]))
          .replaceAll(`{${key}}`, String(values[key]))
          .replaceAll(`{{ ${key} }}`, String(values[key]))
          .replaceAll(`{{${key}}}`, String(values[key]));
      }, source);
      return this.#decodeHtmlEntities(formatted);
    }

    /**
     * Liquid escapes translation strings for data attributes. Decode them before
     * assigning to textContent so entities such as &#39; render as normal text.
     * @param {string} text
     * @returns {string}
     */
    #decodeHtmlEntities(text) {
      if (!text.includes('&')) return text;
      const textarea = document.createElement('textarea');
      textarea.innerHTML = text;
      return textarea.value;
    }

    /**
     * @param {string} text
     * @param {'approaching'|'met'} state
     */
    #setProgressText(text, state) {
      if (!this.#progressEl) return;
      this.#progressEl.textContent = text;
      this.#progressEl.dataset.state = state;
    }

    /* ── Multi-Size Selector ── */

    /** @param {AbortSignal} signal */
    #initMultiSizeSelector(signal) {
      if (!this.#sizeRows) return;

      // Delegate events on the size rows container
      this.#sizeRows.addEventListener('click', (e) => {
        const target = /** @type {HTMLElement} */ (e.target);
        const btn = target.closest('[data-action]');
        if (!btn) return;

        const row = btn.closest('[data-size-row]');
        if (!row) return;

        const input = /** @type {HTMLInputElement|null} */ (row.querySelector('.ra-mb__size-input'));
        if (!input) return;

        const action = /** @type {HTMLElement} */ (btn).dataset.action;
        let val = parseInt(input.value, 10) || 0;

        if (action === 'plus') {
          val++;
        } else if (action === 'minus' && val > 0) {
          val--;
        }

        input.value = String(val);
        this.#updateSizeTotal();
      }, { signal });

      // Add All button
      if (this.#atcBtn) {
        this.#atcBtn.addEventListener('click', () => {
          this.#handleAddAll();
        }, { signal });

        // Keyboard
        this.#atcBtn.addEventListener('keydown', (e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            this.#handleAddAll();
          }
        }, { signal });
      }
    }

    #updateSizeTotal() {
      if (!this.#sizeRows) return;

      const inputs = this.#sizeRows.querySelectorAll('.ra-mb__size-input');
      let total = 0;
      inputs.forEach((input) => {
        total += parseInt(/** @type {HTMLInputElement} */ (input).value, 10) || 0;
      });

      // Update the total display
      if (this.#totalEl) {
        const totalLabel = this.#formatTemplate(this.dataset.totalItemsTemplate, {
          count: total
        }, `${total} item${total !== 1 ? 's' : ''}`);
        if (this.#offerType === 'bxgy') {
          const totalNeeded = this.#bxgyBuy + this.#bxgyGet;
          const freeCount = totalNeeded > 0 ? Math.floor(total / totalNeeded) * this.#bxgyGet : 0;
          const unlockedTemplate = freeCount === 1
            ? this.dataset.bxgyUnlockedOneTemplate
            : this.dataset.bxgyUnlockedOtherTemplate;
          this.#totalEl.textContent = freeCount > 0
            ? `${totalLabel} · ${this.#formatTemplate(unlockedTemplate, { count: freeCount }, `${freeCount} free`)}`
            : totalLabel;
        } else {
          this.#totalEl.textContent = totalLabel;
        }
      }

      // Enable/disable Add All button
      if (this.#atcBtn) {
        /** @type {HTMLButtonElement} */ (this.#atcBtn).disabled = total === 0;
      }

      // Update the progress bar with total from multi-size selector
      this.#updateProgress(total);
    }

    async #handleAddAll() {
      if (!this.#sizeRows || !this.#atcBtn) return;

      const rows = this.#sizeRows.querySelectorAll('[data-size-row]');
      const items = [];

      rows.forEach((row) => {
        const input = /** @type {HTMLInputElement|null} */ (row.querySelector('.ra-mb__size-input'));
        const variantId = /** @type {HTMLElement} */ (row).dataset.variantId;
        const qty = parseInt(input?.value || '0', 10);

        if (qty > 0 && variantId) {
          items.push({
            id: parseInt(variantId, 10),
            quantity: qty
          });
        }
      });

      if (items.length === 0) return;

      const addedQuantity = items.reduce((total, item) => total + item.quantity, 0);

      // Set loading state
      this.#setAtcState('loading', this.dataset.btnAdding || 'Adding...');

      // Abort any previous request
      if (this.#fetchController) {
        this.#fetchController.abort();
      }
      this.#fetchController = new AbortController();

      try {
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          body: JSON.stringify({ items }),
          signal: this.#fetchController.signal
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => null);
          const msg = errorData?.description || errorData?.message || 'Could not add items';
          throw new Error(msg);
        }

        // Success
        this.#setAtcState('success', this.dataset.btnAdded || 'Added!');

        // Reset quantities after successful add
        const inputs = this.#sizeRows.querySelectorAll('.ra-mb__size-input');
        inputs.forEach((input) => {
          /** @type {HTMLInputElement} */ (input).value = '0';
        });
        this.#updateSizeTotal();

        await this.#refreshThemeCart(addedQuantity, items);

        // Reset button after a delay
        if (this.#successTimeout) clearTimeout(this.#successTimeout);
        this.#successTimeout = setTimeout(() => {
          this.#setAtcState('idle', this.dataset.btnIdle || 'Add All to Cart');
        }, 2500);

      } catch (err) {
        if (err.name === 'AbortError') return;

        this.#setAtcState('error', err.message || this.dataset.btnError || 'Error');

        // Reset button after a delay
        if (this.#successTimeout) clearTimeout(this.#successTimeout);
        this.#successTimeout = setTimeout(() => {
          this.#setAtcState('idle', this.dataset.btnIdle || 'Add All to Cart');
        }, 3500);
      } finally {
        this.#fetchController = null;
      }
    }

    /**
     * @param {'idle'|'loading'|'success'|'error'} state
     * @param {string} text
     */
    #setAtcState(state, text) {
      if (!this.#atcBtn) return;

      if (state === 'idle') {
        delete this.#atcBtn.dataset.state;
      } else {
        this.#atcBtn.dataset.state = state;
      }

      this.#atcBtn.textContent = text;
      /** @type {HTMLButtonElement} */ (this.#atcBtn).disabled = state === 'loading';
    }

    /**
     * Attempt to refresh the Horizon theme cart by fetching /cart.js
     * and dispatching the events the theme listens for.
     */
    async #refreshThemeCart(addedQuantity, addedItems) {
      try {
        const resp = await fetch('/cart.js', {
          headers: { 'Accept': 'application/json' }
        });
        const cart = await resp.json();

        document.dispatchEvent(new CustomEvent('cart:update', {
          bubbles: true,
          detail: {
            resource: cart,
            sourceId: this.id || 'ra-multi-buy',
            data: {
              itemCount: cart.item_count,
              source: 'ra-multi-buy'
            }
          }
        }));

        document.dispatchEvent(new CustomEvent('cart:refresh', {
          bubbles: true,
          detail: { cart }
        }));

        document.dispatchEvent(new CustomEvent('ra:cart:synchronized', {
          bubbles: true,
          detail: { cart }
        }));

        document.dispatchEvent(new CustomEvent('ra:multi-buy:added', {
          bubbles: true,
          detail: {
            itemCount: cart.item_count,
            addedQuantity,
            variantIds: addedItems.map((item) => item.id)
          }
        }));
      } catch (_) {
        // Silently fail — the cart will refresh on next page load
      }
    }
  }

  customElements.define('multi-buy-offer', MultiBuyOffer);
}
